import { useEffect } from "react"
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query"
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react"
import { Toaster } from "sonner"
import { toast } from "sonner"
import { useNavigate } from "react-router-dom"
import { env } from "@/lib/env"
import { ApiAuthBridge } from "@/lib/auth/ApiAuthBridge"
import {
  isUnauthorizedError,
  isForbiddenError,
  setSessionExpiryHandler,
} from "@/lib/api/apiClient"

/**
 * Global error handler for API auth errors.
 *
 * 401 is handled by the session-expiry bridge below (toast + Auth0
 * logout + redirect). We only handle 403 here since that's a real
 * "you can't do this thing" message rather than session expiry.
 */
function handleGlobalApiError(error: unknown) {
  if (isUnauthorizedError(error)) return
  if (isForbiddenError(error)) {
    toast.error("Not authorized to perform this action.")
  }
}

/**
 * Wires the apiClient session-expiry callback to Auth0's logout.
 * On the first 401, shows a single deduplicated toast then logs the user
 * out via Auth0 (which clears the SDK cache and redirects to /sign-in
 * with a fresh, unauthenticated state — no infinite-redirect loop).
 */
function Auth0LogoutBridge() {
  const { logout } = useAuth0()
  useEffect(() => {
    setSessionExpiryHandler(() => {
      toast.error("Session expired. Redirecting to sign-in…", {
        id: "session-expired",
        duration: 4000,
      })
      setTimeout(() => {
        logout({
          logoutParams: { returnTo: window.location.origin + "/sign-in" },
        })
      }, 800)
    })
  }, [logout])
  return null
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: (failureCount, error) => {
        // Do not retry auth errors — they won't resolve on retry
        if (isUnauthorizedError(error) || isForbiddenError(error)) return false
        return failureCount < 1
      },
    },
  },
  queryCache: new QueryCache({
    onError: handleGlobalApiError,
  }),
  mutationCache: new MutationCache({
    onError: handleGlobalApiError,
  }),
})

interface ProvidersProps {
  children: React.ReactNode
}

function Auth0ProviderWithRedirect({ children }: ProvidersProps) {
  const navigate = useNavigate()

  return (
    <Auth0Provider
      domain={env.VITE_AUTH0_DOMAIN}
      clientId={env.VITE_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: (env.VITE_AUTH0_REDIRECT_URI ?? window.location.origin + "/sign-in"),
        audience: env.VITE_AUTH0_AUDIENCE,
      }}
      onRedirectCallback={(appState) => {
        navigate(appState?.returnTo || "/companies", { replace: true })
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
    >
      {children}
    </Auth0Provider>
  )
}

export function Providers({ children }: ProvidersProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <Auth0ProviderWithRedirect>
        <ApiAuthBridge>
          <Auth0LogoutBridge />
          {children}
          <Toaster position="top-right" richColors />
        </ApiAuthBridge>
      </Auth0ProviderWithRedirect>
    </QueryClientProvider>
  )
}
